import { SIGN_OUT } from 'store/auth/actions';
import {
  TaskDetailsState,
  TasksAssignedDefaultFilter,
  TasksAssignedDefaultFilterOptions,
} from 'types/Tasks';
import { createReducer } from 'typesafe-actions';
import {
  doTasksAssignedFilterOptionsLoad,
  doTasksAssignedFilterOptionsLoadFailure,
  doTasksAssignedFilterOptionsLoadSuccess,
  doTaskAssignedDetailsLoadFailure,
  doTaskAssignedDetailsLoadSuccess,
  doTasksAssignedDetailsLoad,
  doTasksAssignedLoad,
  doTasksAssignedLoadFailure,
  doTasksAssignedLoadSuccess,
  doTasksAssignedSetFilter,
  doTasksAssignedClearDraftFilterOptions,
  doTasksAssignedDetailsClose,
  doTaskAssignedStatusSaveSuccess,
  doTaskAssignedCommentCreateSuccess,
} from './actions';
import { TasksState } from './types';

const createEmptyTaskDetailsState = (): TaskDetailsState => ({
  isLoading: false,
  error: null,
  id: null,
  item: null,
});

const initialState: TasksState = {
  tasksAssigned: {
    items: [],
    dueGroups: [],
    selectedId: null,
    isLoading: false,
    isLoaded: false,
    filter: TasksAssignedDefaultFilter,
    filterOptions: TasksAssignedDefaultFilterOptions,
    filterOptionsDraft: null,
    filterOptionsLoadStatus: null,
    taskDetails: createEmptyTaskDetailsState(),
    taskPagination: {
      page: 0,
      limit: 50,
      total: 0,
      totalPages: 0,
      statusTotals: [],
    },
  },
};

export const tasksReducer = createReducer(initialState)
  .handleAction(SIGN_OUT.success, () => initialState)
  .handleAction(doTasksAssignedLoad, (state, action) => ({
    ...state,
    tasksAssigned: action.payload.showBlockingLoader
      ? state.tasksAssigned
      : {
          ...state.tasksAssigned,
          isLoading: true,
          isLoaded:
            typeof action.payload.page === 'number' && action.payload.page > 1
              ? state.tasksAssigned.isLoaded
              : false,
          filter: action.payload.filter
            ? { ...state.tasksAssigned.filter, ...action.payload.filter }
            : state.tasksAssigned.filter,
          error: undefined,
        },
  }))
  .handleAction(doTasksAssignedLoadSuccess, (state, action) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      items: action.payload.append
        ? [...state.tasksAssigned.items, ...action.payload.tasks]
        : action.payload.tasks,
      dueGroups: action.payload.append
        ? Array.from(
            new Map(
              [
                ...state.tasksAssigned.dueGroups,
                ...action.payload.dueGroups,
              ].map((group) => [group.id, group]),
            ).values(),
          )
        : action.payload.dueGroups,
      isLoading: false,
      isLoaded: true,
      error: undefined,
      taskPagination: action.payload.pagination,
    },
  }))
  .handleAction(doTasksAssignedLoadFailure, (state, action) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      isLoading: false,
      isLoaded: !!action.payload.error,
      error: action.payload.error,
    },
  }))
  .handleAction(doTasksAssignedSetFilter, (state, action) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      filter: action.payload,
    },
  }))
  .handleAction(doTasksAssignedFilterOptionsLoad, (state) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      filterOptionsLoadStatus: 'loading',
    },
  }))
  .handleAction(doTasksAssignedClearDraftFilterOptions, (state) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      filterOptionsDraft: null,
    },
  }))
  .handleAction(doTasksAssignedFilterOptionsLoadSuccess, (state, action) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      filterOptions: !action.payload.draft
        ? action.payload.filterOptions
        : state.tasksAssigned.filterOptions,
      filterOptionsDraft: action.payload.draft
        ? action.payload.filterOptions
        : state.tasksAssigned.filterOptionsDraft,
      filterOptionsLoadStatus: 'loaded',
    },
  }))
  .handleAction(doTasksAssignedFilterOptionsLoadFailure, (state) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      filterOptionsLoadStatus: 'error',
    },
  }))
  .handleAction(doTasksAssignedDetailsLoad, (state, action) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      taskDetails: {
        ...createEmptyTaskDetailsState(),
        id: action.payload.taskId,
        isLoading: true,
      },
    },
  }))
  .handleAction(doTaskAssignedDetailsLoadSuccess, (state, action) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      taskDetails: {
        item: action.payload.task,
        id: action.payload.task.id,
        isLoading: false,
        error: null,
      },
    },
  }))
  .handleAction(doTaskAssignedDetailsLoadFailure, (state, action) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      taskDetails: {
        ...createEmptyTaskDetailsState(),
        isLoading: false,
        error: action.payload.error,
      },
    },
  }))
  .handleAction(doTaskAssignedStatusSaveSuccess, (state, action) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      // Keep the assigned tasks list in sync
      items: state.tasksAssigned.items.map((task) =>
        task.id === action.payload.taskId
          ? {
              ...task,
              statusName: action.payload.status.statusName,
              statusType: action.payload.status.statusType,
            }
          : task,
      ),
      // Keep the currently loaded task detail in sync
      taskDetails: {
        ...state.tasksAssigned.taskDetails,
        item:
          state.tasksAssigned.taskDetails.item?.id === action.payload.taskId
            ? {
                ...state.tasksAssigned.taskDetails.item,
                activeStatus: action.payload.status,
                comments: action.payload.statusChangeComment
                  ? [
                      action.payload.statusChangeComment,
                      ...state.tasksAssigned.taskDetails.item.comments,
                    ]
                  : state.tasksAssigned.taskDetails.item.comments,
              }
            : state.tasksAssigned.taskDetails.item,
      },
    },
  }))
  .handleAction(doTaskAssignedCommentCreateSuccess, (state, action) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      taskDetails: {
        ...state.tasksAssigned.taskDetails,
        item:
          state.tasksAssigned.taskDetails.item?.id === action.payload.taskId
            ? {
                ...state.tasksAssigned.taskDetails.item,
                // Add the response to the top of comments
                comments: [
                  action.payload.comment,
                  ...state.tasksAssigned.taskDetails.item.comments,
                ],
              }
            : state.tasksAssigned.taskDetails.item,
      },
    },
  }))
  .handleAction(doTasksAssignedDetailsClose, (state) => ({
    ...state,
    tasksAssigned: {
      ...state.tasksAssigned,
      taskDetails: {
        ...createEmptyTaskDetailsState(),
      },
    },
  }));
